import type { UIStrings } from './fr';

/**
 * Interface labels — English.
 *
 * Typed as `UIStrings`: a missing or unknown key fails `npm run typecheck`.
 * Editorial content lives in `src/data/en/`.
 */
export const en: UIStrings = {
  nav: {
    ariaLabel:     'Main navigation',
    home:          'Home — Pierre Séré',
    expertise:     'Expertise',
    cnc:           'CNC Tracy',
    asterion:      'Asterion',
    projects:      'Projects',
    articles:      'Articles',
    skills:        'Stack',
    timeline:      'Career',
    contact:       'Contact',
    founder:       'The founder',
    toLight:       'Switch to light mode',
    toDark:        'Switch to dark mode',
    switchLang:    'Lire cette page en français',
  },

  hero: {
    location:      'Sorel-Tracy, Quebec · Canada',
    typed: [
      'Mechanical Engineering Technician',
      'Quality Inspector · ISO 9001 · CMMS',
      'Dimensional Inspection · GD&T · NDT',
      'Project Management · Continuous Improvement',
      'Founder of Asterion Solutions',
      'OSEntreprendre 2026 Laureate',
    ],
    bio:
      'Mechanical engineering technician in Sorel-Tracy (Montérégie, Quebec), I turn ' +
      'manufacturing operations into systems that are simpler, faster and ' +
      'more reliable. Not a developer — a shop-floor quality inspector who builds the ' +
      'tools and methods the industry did not have yet.',
    yearsLabel:    'years in the trade',
    reportsValue:  '1000+',
    reportsLabel:  'reports generated with Asterion',
    ctaContact:    'Get in touch',
    ctaJourney:    'See my career',
    scroll:        'Scroll',
    scrollAria:    'Scroll down to expertise',
  },

  photo: {
    stamp:         'INSPECTED',
    award:         'OSEntreprendre · 2026',
    refLabel:      'REF',
    expLabel:      'EXP',
    expValue:      '14 YEARS',
    statusLabel:   'STATUS',
    statusValue:   'ACTIVE',
    alt:           'Pierre Séré, mechanical engineering technician and founder of Asterion Solutions',
  },

  intro: {
    tagline:       'Mechanical Engineering · Founder of Asterion Solutions · OSEntreprendre 2026 Laureate',
    role:          'Technician · Builder',
    enter:         'Enter',
    press:         'Press',
  },

  sections: {
    expertise: {
      chip:        'Expertise',
      title:       'Shop Floor &\nPrecision',
      subtitle:    'Fourteen years of industrial mechanical engineering in Quebec — dimensional inspection, GD&T, NDT, ISO 9001 quality systems.',
    },
    cnc: {
      chip:        'CNC Tracy',
      title:       'In Production\nAt CNC Tracy',
      subtitle:    'A secure internal application hub — Microsoft 365 authentication, every app tied into the Notixia ERP, in production since 2023.',
      masterpiece: 'Flagship',
    },
    asterion: {
      chip:        'Asterion',
      title:       'The Asterion\nEcosystem',
      subtitle:    'A suite of industrial B2B SaaS applications — built from scratch, running in production.',
      cta:         'See Asterion Solutions ↗',
      ctaSub:      'Industrial B2B SaaS · Multi-tenant · In production',
      comingSoon:  'Coming soon',
      shotAlt:     'Screenshot of',
      enlarge:     'Enlarge screenshot',
      close:       'Close',
    },
    projects: {
      chip:        'Projects',
      title:       'Personal\nProjects',
      subtitle:    'Prototypes, experiments, and answers to real problems.',
      visit:       'Visit',
    },
    articles: {
      chip:        'Articles',
      title:       'My\nArticles',
      subtitle:    'I write about artificial intelligence applied to industrial quality in small and mid-sized manufacturers. In-depth pieces, also published on the Asterion Solutions blog.',
      readMore:    'Read the article →',
    },
    skills: {
      chip:        'Profile',
      title:       'Skills\n& Tools',
      subtitle:    'Fourteen years of shop-floor expertise plus applied AI. Not demos — industrial tools running in production.',
    },
    timeline: {
      chip:        'Career',
      title:       'Professional\nTimeline',
      subtitle:    'From CNC machinist in Sorel-Tracy to startup founder — every step built the next one.',
    },
    contact: {
      chip:        'Contact',
      title:       "Let's\nTalk",
      subtitle:    'A job, a challenge, freelance work or Asterion Solutions — if one of these sounds like you, the conversation is worth having.',
      available:   'Open to opportunities',
    },
    pillars: {
      heading:     'Three levers, one goal: an organization that performs better',
      title:       'What I bring',
    },
    rupture: {
      chip:        'What sets me apart',
    },
  },

  articles: {
    portfolio:     '← Portfolio',
    indexKicker:   'The blog',
    indexTitle:    'Quality, AI & manufacturing SMEs',
    indexIntro:    'In-depth articles written from the shop floor, on what artificial intelligence does — and does not — change for shops that want to structure their quality.',
    indexCount:    'expertise pieces',
    exploreByTheme:'Browse by theme',
    indexBack:     '← Back to home',
    backToIndex:   '← All articles',
    by:            'By',
    readingTime:   'min read',
    tocTitle:      'Contents',
    readOnAsterion:'Read on the Asterion blog →',
    frenchOnly:    'Article available in French only',
    browseAll:     'Browse all articles →',
    seeOnAsterion: 'View on Asterion ↗',
    blogUrl:       'https://solutions-asterion.com/en/blog',
  },

  founder: {
    intro:       'How a quality technician started coding for his own shop, and how that turned into a company.',
    ctaText:     'The application catalog, pricing and terms are on the company site.',
    ctaLink:     'Visit Asterion Solutions ↗',
    relatedAria: 'Related pages',
    toTimeline:  'The detailed career of Pierre Séré →',
    toArticles:  'The in-depth articles →',
  },

  footer: {
    rights:        'All rights reserved',
    builtWith:     'Built with React, TypeScript and Firebase',
  },
};
